import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Csv, DataModel, MeModel } from '@blazemeter/blaze-data-model';
import { ManipulatorDataModel, MeManipulatorDM } from '@blazemeter/blaze-data-designer-client';
import { ApplicationState } from '../../store/rootReducer';
import { getDataModelForService } from '../../store/dataModel/dataModelActions';
import blazeDataClient from '../../blazeData/blazeDataClient';
import axios from '../../axios/axios-base';
import acApi from '../../axios/ac/acApi';
import {
  extractPropertyGroupsFromManipulatorModel,
  getPreviewTitleSuffix,
  mergeOverrideWithDataModel,
} from '../../utils/blazeDataUtils';
import { getTableDataFromObject } from '../../utils/tableEditorHelper';
import { DataPreviewSettings, IData } from '@blazemeter/blaze-data-ui';
import FullPageLoader from '../loaders/FullPageLoader';
import BlazeDataEntitiesAccordion, {
  GLOBAL_SCOPE_KEY,
  SQL_BOOTSTRAP_SCRIPT,
} from '../../views/MockServices/Tabs/MockServices/DataSettings/Accordion/BlazeDataEntitiesAccordion';
import MockServicePreviewDataSample from '../../views/MockServices/Tabs/MockServices/DataSettings/ContentPane/MockServicePreviewDataSample';
import GlobalScopePreview from '../../views/MockServices/Tabs/MockServices/DataSettings/ContentPane/GlobalScopePreview';
import { MSDataPreview } from '../../views/MockServices/Tabs/MockServices/DataSettings/MockServiceDataSettings';
import { SandBoxTx } from '../../types/rest/assetCatalogTypes';

interface SandboxDataPreviewModalProps {
  serviceId: number;
  onClose: () => void;
}

interface ModalState {
  isLoadingModel: boolean;
  isError: boolean;
  isGenerationPending: boolean;
  selectedDataModel: DataModel | null;
  selectedManipulatorDataModel: ManipulatorDataModel | null;
  activeEntity: string | undefined;
  dataPreviews: { [key: string]: MSDataPreview };
  isGeneratingPreview: boolean;
  sandboxTransaction: SandBoxTx | null;
  datasetStateModels: Record<string, Array<Record<string, unknown>>> | null;
}

const SandboxDataPreviewModal = ({ serviceId, onClose }: SandboxDataPreviewModalProps) => {
  const mockService = useSelector(
    (s: ApplicationState) =>
      s.mockservices.dataList.find(sv => sv.serviceId === serviceId) ?? ({ dataSettings: null, serviceId } as any),
  );

  const [state, setState] = useState<ModalState>({
    isLoadingModel: true,
    isError: false,
    isGenerationPending: false,
    selectedDataModel: null,
    selectedManipulatorDataModel: null,
    activeEntity: undefined,
    dataPreviews: {},
    isGeneratingPreview: false,
    sandboxTransaction: null,
    datasetStateModels: null,
  });

  const isMounted = useRef(true);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      isMounted.current = false;
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  useEffect(() => {
    loadSandboxAndDataModel();
  }, []);

  // Generate preview once the data model is ready and we have a valid preview link
  useEffect(() => {
    if (state.selectedManipulatorDataModel && state.activeEntity) {
      const previewLink = state.sandboxTransaction?.blazeData?.blazeDataDetailLinksDto?.previewFileLink;
      generatePreviewForEntity(
        state.activeEntity,
        state.selectedManipulatorDataModel,
        previewLink,
        state.datasetStateModels,
      );
    }
  }, [state.selectedManipulatorDataModel]);

  const fetchDatasetStateModels = async (): Promise<Record<string, Array<Record<string, unknown>>> | null> => {
    try {
      const stateResult = (await acApi.sandbox.getDatasetState())?.data?.result;
      const models = stateResult?.models;
      if (models && Object.keys(models).length > 0) {
        return models;
      }
    } catch {
      // non-fatal — fall back to cloud file
    }
    return null;
  };

  const loadSandboxAndDataModel = async () => {
    try {
      // Fetch fresh sandbox state so we always see the latest generation result,
      // matching what "Test Request" reads from DB at execution time.
      const [sandboxResult, datasetStateModels] = await Promise.all([
        acApi.sandbox.getSandbox().then(r => r?.data?.result),
        fetchDatasetStateModels(),
      ]);
      if (!isMounted.current) return;

      if (sandboxResult?.blazeDataGenerationCompleted !== true) {
        setState(prev => ({ ...prev, isLoadingModel: false, isGenerationPending: true, datasetStateModels }));
        pollingRef.current = setInterval(async () => {
          try {
            const polled = (await acApi.sandbox.getSandbox())?.data?.result;
            if (!isMounted.current) return;
            if (polled?.blazeDataGenerationCompleted === true) {
              clearInterval(pollingRef.current!);
              pollingRef.current = null;
              setState(prev => ({ ...prev, isGenerationPending: false, isLoadingModel: true }));
              await loadDataModel(polled, datasetStateModels);
            }
          } catch {
            // keep polling
          }
        }, 3000);
        return;
      }

      await loadDataModel(sandboxResult, datasetStateModels);
    } catch {
      if (isMounted.current) {
        setState(prev => ({ ...prev, isLoadingModel: false, isError: true }));
      }
    }
  };

  const loadDataModel = async (
    sandboxResult: SandBoxTx,
    datasetStateModels: Record<string, Array<Record<string, unknown>>> | null,
  ) => {
    try {
      const dataModel = await getDataModelForService(serviceId);
      if (!isMounted.current) return;
      if (!dataModel) {
        setState(prev => ({ ...prev, isLoadingModel: false, sandboxTransaction: sandboxResult, datasetStateModels }));
        return;
      }
      const manipulatorModel = await blazeDataClient.manipulator.load(dataModel);
      if (!isMounted.current) return;
      const entities = (dataModel as MeModel).entities;
      const firstEntity = _.keys(entities)[0];
      setState(prev => ({
        ...prev,
        selectedDataModel: dataModel,
        selectedManipulatorDataModel: manipulatorModel,
        activeEntity: firstEntity,
        isLoadingModel: false,
        sandboxTransaction: sandboxResult,
        datasetStateModels,
      }));
    } catch {
      if (isMounted.current) {
        setState(prev => ({ ...prev, isLoadingModel: false, isError: true }));
      }
    }
  };

  const SANDBOX_ROW_COUNT = 10;

  const generatePreviewFromLiveModel = async (entity: string, manipulatorModel: ManipulatorDataModel) => {
    const extractedModel = (await blazeDataClient.manipulator.extractDataModel(
      manipulatorModel,
      (manipulatorModel as MeManipulatorDM).entities[entity].dataset.id,
    )) as MeModel;

    // Force sandbox row count — the model may have its own repeat setting from TDM
    // (e.g. "1") that would override the preview size passed to generateStoredModel.
    // Merging the override here ensures we always generate exactly SANDBOX_ROW_COUNT rows.
    extractedModel.entities = mergeOverrideWithDataModel(extractedModel.entities, {
      [entity]: { repeat: SANDBOX_ROW_COUNT, datasources: [] },
    });

    const generatedResult = await blazeDataClient.manipulator.generator.generateStoredModel(
      extractedModel.id,
      extractedModel,
      undefined,
      SANDBOX_ROW_COUNT,
      entity,
      [] as Csv[],
    );
    if (generatedResult?.isValid && generatedResult[entity]?.data) {
      const entityData = generatedResult[entity];
      const propertyGroups = extractPropertyGroupsFromManipulatorModel(manipulatorModel as MeManipulatorDM, entity);
      const previewTitle = getPreviewTitleSuffix(entityData.data.length, entityData.total);
      const tableData = getTableDataFromObject(propertyGroups, entityData.data);
      const preview: MSDataPreview = {
        settings: { editable: false, title: previewTitle } as DataPreviewSettings,
        rows: tableData.rows as IData,
        tableHeaders: tableData.tableHeaders,
        isFixed: false,
        downloadLink: undefined,
      };
      if (isMounted.current) {
        setState(prev => ({
          ...prev,
          dataPreviews: { ...prev.dataPreviews, [entity]: preview },
          isGeneratingPreview: false,
        }));
      }
    } else {
      if (isMounted.current) {
        setState(prev => ({ ...prev, isGeneratingPreview: false, isError: true }));
      }
    }
  };

  const generatePreviewForEntity = async (
    entity: string,
    manipulatorModel: ManipulatorDataModel,
    link: string | undefined,
    datasetStateModels?: Record<string, Array<Record<string, unknown>>> | null,
  ) => {
    setState(prev => ({ ...prev, isGeneratingPreview: true }));
    try {
      // If State Update has fired this session, show the current in-memory cache state.
      // We trigger extractDataModel first so the manipulator model loads entity metadata
      // (propertiesGroups) before we call extractPropertyGroupsFromManipulatorModel.
      const liveRows = datasetStateModels?.[entity];
      if (liveRows?.length) {
        try {
          await blazeDataClient.manipulator.extractDataModel(
            manipulatorModel,
            (manipulatorModel as MeManipulatorDM).entities[entity].dataset.id,
          );
        } catch {
          // entity metadata not available — fall through to cloud file / live model path
        }
        const propertyGroups = extractPropertyGroupsFromManipulatorModel(manipulatorModel as MeManipulatorDM, entity);
        if (propertyGroups.length > 0) {
          const previewTitle = getPreviewTitleSuffix(liveRows.length, liveRows.length);
          const tableData = getTableDataFromObject(propertyGroups, liveRows as Array<{ [key: string]: unknown }>);
          const preview: MSDataPreview = {
            settings: { editable: false, title: previewTitle } as DataPreviewSettings,
            rows: tableData.rows as IData,
            tableHeaders: tableData.tableHeaders,
            isFixed: false,
            downloadLink: undefined,
          };
          if (isMounted.current) {
            setState(prev => ({
              ...prev,
              dataPreviews: { ...prev.dataPreviews, [entity]: preview },
              isGeneratingPreview: false,
            }));
          }
          return;
        }
        // propertyGroups empty (entity metadata not loaded yet) — fall through to cloud file
        // path so the initial display is still meaningful; subsequent entity switches will
        // hit the live rows path with properly loaded metadata.
      }
      // Try reading from the sandbox-generated data file first
      if (link) {
        const blazeData = (await axios.get(link)).data;
        if (blazeData.isValid && blazeData[entity]?.data?.length) {
          const generatedData = blazeData[entity];
          const propertyGroups = extractPropertyGroupsFromManipulatorModel(manipulatorModel as MeManipulatorDM, entity);
          const previewTitle = getPreviewTitleSuffix(generatedData.data.length, generatedData.total);
          const tableData = getTableDataFromObject(propertyGroups, generatedData.data);
          const preview: MSDataPreview = {
            settings: { editable: false, title: previewTitle } as DataPreviewSettings,
            rows: tableData.rows as IData,
            tableHeaders: tableData.tableHeaders,
            isFixed: false,
            downloadLink: undefined,
          };
          if (isMounted.current) {
            setState(prev => ({
              ...prev,
              dataPreviews: { ...prev.dataPreviews, [entity]: preview },
              isGeneratingPreview: false,
            }));
          }
          return;
        }
      }
      // Sandbox file is missing or stale for this entity (e.g. a new Data Parameter was added
      // after the sandbox file was generated). Fall back to live client-side generation so the
      // current model is always reflected without waiting for a full sandbox re-generation.
      await generatePreviewFromLiveModel(entity, manipulatorModel);
    } catch {
      if (isMounted.current) {
        setState(prev => ({ ...prev, isGeneratingPreview: false, isError: true }));
      }
    }
  };

  const onEntityChanged = async (entityId: string | undefined) => {
    setState(prev => ({ ...prev, activeEntity: entityId }));
    if (
      entityId &&
      entityId !== GLOBAL_SCOPE_KEY &&
      entityId !== SQL_BOOTSTRAP_SCRIPT &&
      state.selectedManipulatorDataModel &&
      !state.dataPreviews[entityId]
    ) {
      const previewLink = state.sandboxTransaction?.blazeData?.blazeDataDetailLinksDto?.previewFileLink;
      await generatePreviewForEntity(
        entityId,
        state.selectedManipulatorDataModel,
        previewLink,
        state.datasetStateModels,
      );
    }
  };

  return (
    <Modal
      show={true}
      enforceFocus={false}
      centered={true}
      backdrop={true}
      dialogClassName="modal-full-screen"
      onHide={onClose}
    >
      <Modal.Header closeButton className={'py-4'}>
        <h5 className="modal-title">Data Preview</h5>
      </Modal.Header>
      <Modal.Body>
        {state.isLoadingModel || state.isGenerationPending ? (
          <FullPageLoader
            className={'pt-10'}
            loadingText={state.isGenerationPending ? 'Generating data...' : 'Loading Service Data...'}
          />
        ) : (
          <div className="row">
            <div className="col-lg-3">
              <BlazeDataEntitiesAccordion
                mockService={mockService}
                dataModel={state.selectedDataModel}
                activeEntity={state.activeEntity}
                onActiveEntityChanged={onEntityChanged}
                onUpdateIterationOverrides={_.noop}
                disabled={true}
                hideSqlBootstrap={true}
                overrideRepeatCount={10}
              />
            </div>
            <div className="col-lg-9">
              {state.activeEntity === GLOBAL_SCOPE_KEY ? (
                <GlobalScopePreview
                  globalScope={
                    state.datasetStateModels?.['GLOBAL_VARIABLES']?.[0] ??
                    state.sandboxTransaction?.blazeData?.globalVariables ??
                    {}
                  }
                  dataCount={_.size(
                    state.datasetStateModels?.['GLOBAL_VARIABLES']?.[0] ??
                      state.sandboxTransaction?.blazeData?.globalVariables,
                  )}
                  refreshBtn={undefined}
                  isExportInProgress={false}
                  isFixedData={true}
                />
              ) : (
                state.selectedDataModel && (
                  <MockServicePreviewDataSample
                    key={'entity-preview-' + state.activeEntity}
                    dataModel={state.selectedDataModel}
                    manipulatorDataModel={state.selectedManipulatorDataModel}
                    activeEntity={state.activeEntity}
                    isLoading={state.isGeneratingPreview}
                    dataPreview={
                      state.dataPreviews[state.activeEntity] ?? {
                        rows: [],
                        settings: { editable: false, title: '' },
                        isFixed: false,
                        tableHeaders: null,
                      }
                    }
                    isExportInProgress={false}
                    isError={state.isError}
                  />
                )
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default SandboxDataPreviewModal;
